import React, { useEffect, useRef, useState } from 'react';

interface DropdownProps {
  label: string | React.ReactNode;
  children: React.ReactNode;
}

export function Dropdown({ label, children }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clone children and add onClick handler to close dropdown
  const childrenWithClose = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>, {
        onClick: (e: React.MouseEvent) => {
          child.props.onClick?.(e);
          setIsOpen(false);
        }
      });
    }
    return child;
  });

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`px-4 py-2 border border-gray-200 text-gray-700 hover:text-gray-900 cursor-pointer ${isOpen ? 'bg-gray-100 rounded-t-lg' : 'rounded-lg'}`}
      >
        {label}
      </button>
      {isOpen && (
        <div 
          data-testid="dropdown-menu"
          className="absolute left-0 z-50 w-48 bg-gray-100 border border-t-0 border-gray-200 rounded-b-lg rounded-tr-lg shadow-lg"
        >
          <div className="border-t border-gray-200">
            {childrenWithClose}
          </div>
        </div>
      )}
    </div>
  );
}
